import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FaCheck, FaPlusSquare } from "react-icons/fa";
import { IoTrashOutline } from "react-icons/io5";
import { LuCopyPlus } from "react-icons/lu";
import { MdEdit } from "react-icons/md";
import { TbTableImport } from "react-icons/tb";
import RightTimeApi from "@/API";
import Modal from "@modalSrc/Modal";
import ContentViewModal from "./ContentViewModal";
import TextInputModal from "./TextInputModal";
import SectionEditModal from "./SectionEditModal";
import ContentEditModal from "./ContentEditModal";
import ContentImportModal from "./ContentImportModal";
import ConfirmModal from "./ConfirmModal";
import {
    filterActiveLanguages,
    getLanguageAbbrev,
    getLanguageDisplayName,
    getSectionLanguageContent,
    type LanguageLike,
} from "@/utils/languageUtils";

export default function SectionSelectModal({
    isOpen,
    onClose,
    onSelect,
    onDeselect,
    sectionId = null,
    sectionName = null,
    customSectionId = null,
    companyId,
    updateSpinner,
    mode = "section",
    contentTypeId = null,
    selectedContentId = null,
}) {
    const [customSections, setCustomSections] = useState([]);
    const [supportedLanguages, setSupportedLanguages] = useState<LanguageLike[]>([]);
    const [companies, setCompanies] = useState([]);
    const [selectedCustomSectionId, setSelectedCustomSectionId] = useState(null);
    const [companyFilter, setCompanyFilter] = useState("all");
    const [isLoading, setIsLoading] = useState(false);
    const [showContentViewModal, setShowContentViewModal] = useState(false);
    const [contentViewTitle, setContentViewTitle] = useState("Content");
    const [contentViewHtml, setContentViewHtml] = useState("");
    const [deletingSectionContentId, setDeletingSectionContentId] = useState(null);
    const [deletingCompanyContentId, setDeletingCompanyContentId] = useState(null);
    const [pendingDeleteCompanyContent, setPendingDeleteCompanyContent] = useState(null);
    const [showDeleteCompanyContentConfirm, setShowDeleteCompanyContentConfirm] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [duplicateSourceSectionContentId, setDuplicateSourceSectionContentId] = useState(null);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importingSectionContentId, setImportingSectionContentId] = useState(null);
    const [showSectionEditModal, setShowSectionEditModal] = useState(false);
    const [editingSectionId, setEditingSectionId] = useState(null);
    const [editingCustomSectionId, setEditingCustomSectionId] = useState(null);
    const [editingLanguageAbbrev, setEditingLanguageAbbrev] = useState(null);
    const [updatingCompanySectionContentId, setUpdatingCompanySectionContentId] = useState(null);
    const [isSelecting, setIsSelecting] = useState(false);
    const [contentData, setContentData] = useState({ contents: [], languages: [], contentTypes: [] });
    const [isContentEditModalOpen, setIsContentEditModalOpen] = useState(false);
    const [contentEditId, setContentEditId] = useState(0);
    const [showContentImportModal, setShowContentImportModal] = useState(false);
    const [importContentTypeId, setImportContentTypeId] = useState(null);
    const hasShownMissingTypeErrorRef = useRef(false);

    const isContentMode = mode === "content";
    const targetContentTypeId = Number(contentTypeId || 0);
    const hasValidContentTypeId = targetContentTypeId > 0;

    async function loadCustomSectionsAsync(targetSectionId = sectionId) {
        if (!isOpen || !targetSectionId) return;

        setIsLoading(true);
        if (typeof updateSpinner === "function") updateSpinner(true);

        try {
            const [resp, langResp] = await Promise.all([
                RightTimeApi.getAsync(`/Sections/${targetSectionId}/custom`),
                RightTimeApi.getAsync("/Languages?activeOnly=true"),
            ]);

            if (!resp?.ok) {
                toast.error("Failed to load customized sections.");
                setCustomSections([]);
                setSupportedLanguages([]);
                return;
            }

            const data = resp?.jsonData ?? resp?.data ?? [];
            const list = Array.isArray(data)
                ? data
                : Array.isArray(data?.sections)
                    ? data.sections
                    : Array.isArray(data?.customSections)
                        ? data.customSections
                        : [];

            let nextLanguages: LanguageLike[] = [];
            if (langResp?.ok && Array.isArray(langResp.jsonData) && langResp.jsonData.length > 0) {
                nextLanguages = filterActiveLanguages(langResp.jsonData);
            } else {
                const responseLanguages = Array.isArray(data?.languages) ? data.languages : [];
                nextLanguages = filterActiveLanguages(responseLanguages);
            }

            setSupportedLanguages(nextLanguages);
            setCustomSections(list);
        } catch {
            toast.error("Error loading customized sections.");
            setCustomSections([]);
            setSupportedLanguages([]);
        } finally {
            setIsLoading(false);
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    async function loadContentOptionsAsync() {
        if (!isOpen || !companyId) return;

        setIsLoading(true);
        if (typeof updateSpinner === "function") updateSpinner(true);

        try {
            const resp = await RightTimeApi.getAsync(`/Company/${companyId}/Content`);
            if (!resp?.ok) {
                toast.error("Failed to load company content.");
                setContentData({ contents: [], languages: [], contentTypes: [] });
                setCustomSections([]);
                return;
            }

            const data = resp?.jsonData || { contents: [], languages: [], contentTypes: [] };
            const sortedContents = Array.isArray(data?.contents)
                ? [...data.contents].sort((a, b) => Number(a?.contentType || 0) - Number(b?.contentType || 0))
                : [];

            setContentData({
                contents: sortedContents,
                languages: Array.isArray(data?.languages) ? data.languages : [],
                contentTypes: Array.isArray(data?.contentTypes) ? data.contentTypes : [],
            });
            setCustomSections(sortedContents);
        } catch {
            toast.error("Error loading company content.");
            setContentData({ contents: [], languages: [], contentTypes: [] });
            setCustomSections([]);
        } finally {
            setIsLoading(false);
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    async function loadCompaniesAsync() {
        try {
            const resp = await RightTimeApi.getAsync("/CompaniesSites");
            if (!resp?.ok) {
                setCompanies([]);
                return;
            }

            const rawCompanies =
                resp?.jsonData?.companies ||
                resp?.data?.companies ||
                [];

            const parsedCompanies = (Array.isArray(rawCompanies) ? rawCompanies : [])
                .map((company) => ({
                    id: Number(company?.id || company?.Id || 0),
                    name: company?.name || company?.Name || "",
                }))
                .filter((company) => company.id > 0)
                .sort((a, b) => a.name.localeCompare(b.name));

            setCompanies(parsedCompanies);
        } catch {
            setCompanies([]);
        }
    }

    useEffect(() => {
        if (!isOpen) {
            setCustomSections([]);
            setSupportedLanguages([]);
            setSelectedCustomSectionId(null);
            setShowContentViewModal(false);
            setContentViewTitle("Content");
            setContentViewHtml("");
            setDeletingSectionContentId(null);
            setDeletingCompanyContentId(null);
            setPendingDeleteCompanyContent(null);
            setShowDeleteCompanyContentConfirm(false);
            setShowCreateModal(false);
            setShowDuplicateModal(false);
            setDuplicateSourceSectionContentId(null);
            setShowImportModal(false);
            setImportingSectionContentId(null);
            setShowSectionEditModal(false);
            setEditingSectionId(null);
            setEditingCustomSectionId(null);
            setEditingLanguageAbbrev(null);
            setUpdatingCompanySectionContentId(null);
            setIsSelecting(false);
            setCompanyFilter("all");
            hasShownMissingTypeErrorRef.current = false;
            return;
        }

        const initialSelectedId = isContentMode
            ? Number(selectedContentId || 0)
            : Number(customSectionId || 0);
        setSelectedCustomSectionId(initialSelectedId || null);

        const currentCompanyId = Number(companyId || 0);
        if (currentCompanyId > 0) {
            setCompanyFilter(String(currentCompanyId));
        }
    }, [isOpen, customSectionId, selectedContentId, isContentMode, companyId]);

    useEffect(() => {
        if (!isOpen) return;

        if (isContentMode) {
            if (!hasValidContentTypeId) {
                setCustomSections([]);
                setContentData({ contents: [], languages: [], contentTypes: [] });
                if (!hasShownMissingTypeErrorRef.current) {
                    toast.error("SectionSelectModal requires contentTypeId in content mode.");
                    hasShownMissingTypeErrorRef.current = true;
                }
                return;
            }

            hasShownMissingTypeErrorRef.current = false;
            loadContentOptionsAsync();
            return;
        }

        if (!sectionId) return;
        loadCustomSectionsAsync();
        loadCompaniesAsync();
    }, [isOpen, sectionId, isContentMode, companyId, hasValidContentTypeId]);

    const scrollToSelected = useCallback((node: HTMLTableRowElement | null) => {
        if (node) {
            requestAnimationFrame(() => {
                node.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        }
    }, [selectedCustomSectionId]);

    function getSectionName(section) {
        if (isContentMode) {
            return section?.name || section?.contentTypeName || "";
        }

        return section?.customizedSectionName || section?.CustomName || section?.customName || section?.name || "";
    }

    function getSectionCompanyId(section) {
        const parsedCompanyId = Number(section?.companyId || section?.CompanyId || 0);
        return Number.isFinite(parsedCompanyId) && parsedCompanyId > 0 ? parsedCompanyId : null;
    }

    function getSelectableId(section) {
        const id = isContentMode
            ? Number(section?.contentId || section?.id || section?.Id || 0)
            : Number(
                section?.sectionContentId ||
                section?.SectionContentId ||
                section?.customSectionId ||
                section?.CustomSectionId ||
                section?.id ||
                section?.Id ||
                0
            );

        return Number.isFinite(id) && id > 0 ? id : null;
    }

    const selectedSection = useMemo(
        () => customSections.find((section) => getSelectableId(section) === Number(selectedCustomSectionId)) || null,
        [customSections, selectedCustomSectionId]
    );

    const filteredCustomSections = useMemo(() => {
        if (companyFilter === "all") return customSections;
        if (companyFilter === "") return customSections.filter((section) => getSectionCompanyId(section) === null);

        const filteredCompanyId = Number(companyFilter);
        if (!Number.isFinite(filteredCompanyId) || filteredCompanyId <= 0) return customSections;

        return customSections.filter((section) => getSectionCompanyId(section) === filteredCompanyId);
    }, [customSections, companyFilter]);

    function hasSectionContent(value) {
        if (value === null || value === undefined) return false;
        if (typeof value === "string") return value.trim().length > 0;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === "object") return Object.keys(value).length > 0;
        return Boolean(value);
    }

    async function handleSelectClick() {
        if (!selectedSection || !onSelect) return;

        if (isContentMode) {
            setIsSelecting(true);
            if (typeof updateSpinner === "function") updateSpinner(true);

            try {
                await onSelect(selectedSection);
            } finally {
                setIsSelecting(false);
                if (typeof updateSpinner === "function") updateSpinner(false);
            }
            return;
        }

        const currentCompanyId = Number(companyId || 0);
        const selectedCompanyId = getSectionCompanyId(selectedSection);
        const selectedSectionContentId = getSelectableId(selectedSection);

        setIsSelecting(true);
        if (typeof updateSpinner === "function") updateSpinner(true);

        try {
            let sectionToSelect = selectedSection;

            if (currentCompanyId > 0 && selectedSectionContentId && selectedCompanyId !== currentCompanyId) {
                const copyResp = await RightTimeApi.postAsync(`/SectionContent/${selectedSectionContentId}/copyToCompany`, {
                    companyId: currentCompanyId,
                });

                if (!copyResp?.ok) {
                    toast.error("Failed to copy section content to the current company.");
                    return;
                }

                const copiedContentId = Number(
                    copyResp?.jsonData?.sectionContentId ||
                    copyResp?.jsonData?.id ||
                    copyResp?.data?.sectionContentId ||
                    copyResp?.data?.id ||
                    0
                ) || null;

                const copiedName =
                    copyResp?.jsonData?.sectionContentName ||
                    copyResp?.data?.sectionContentName ||
                    getSectionName(selectedSection);

                const copiedCompanyName =
                    copyResp?.jsonData?.companyName ||
                    copyResp?.data?.companyName ||
                    (companies.find((c) => c.id === currentCompanyId)?.name || "");

                if (copiedContentId) {
                    sectionToSelect = {
                        ...selectedSection,
                        sectionContentId: copiedContentId,
                        SectionContentId: copiedContentId,
                        id: copiedContentId,
                        Id: copiedContentId,
                        name: copiedName,
                        customName: copiedName,
                        customizedSectionName: copiedName,
                        companyId: currentCompanyId,
                        CompanyId: currentCompanyId,
                        companyName: copiedCompanyName,
                        CompanyName: copiedCompanyName,
                    };

                    setCustomSections((prev) => {
                        const exists = prev.some((item) => getSelectableId(item) === copiedContentId);
                        if (exists) return prev;
                        return [...prev, sectionToSelect];
                    });
                }
            }

            await onSelect(sectionToSelect);
        } finally {
            setIsSelecting(false);
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    async function handleDeselectClick() {
        if (!onDeselect) return;

        setIsSelecting(true);
        if (typeof updateSpinner === "function") updateSpinner(true);

        try {
            await onDeselect();
        } finally {
            setIsSelecting(false);
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    function resolveHtmlContent(value) {
        if (typeof value === "string") return value;
        if (value && typeof value === "object") {
            if (typeof value.html === "string") return value.html;
            if (typeof value.content === "string") return value.content;
        }
        return "";
    }

    function resolveLanguageMeta(abbrev: string) {
        const normalizedAbbrev = String(abbrev || "").trim().toLowerCase();
        return supportedLanguages.find((lang) => getLanguageAbbrev(lang).toLowerCase() === normalizedAbbrev)
            || { abbrev: normalizedAbbrev, name: normalizedAbbrev };
    }

    function onViewLanguageContent(e, section, language) {
        e.stopPropagation();

        const rawContent = getSectionLanguageContent(section, language, supportedLanguages);
        const html = resolveHtmlContent(rawContent);
        if (!html || html.trim().length === 0) return;

        const sectionName = getSectionName(section) || "Section";
        const languageName = getLanguageDisplayName(resolveLanguageMeta(language));

        setContentViewTitle(`${sectionName} - ${languageName}`);
        setContentViewHtml(html);
        setShowContentViewModal(true);
    }

    function onCloseContentViewModal() {
        setShowContentViewModal(false);
        setContentViewTitle("Content");
        setContentViewHtml("");
    }

    function openSectionEditModal(e, section, languageAbbrev = null) {
        e?.stopPropagation?.();
        const sectionToEditId = getSelectableId(section);
        if (!sectionToEditId) {
            toast.error("Unable to edit: invalid section content id.");
            return;
        }

        if (typeof updateSpinner === "function") updateSpinner(true);

        setEditingSectionId(sectionToEditId);
        setEditingCustomSectionId(getSelectableId(section));
        setEditingLanguageAbbrev(languageAbbrev);
        setShowSectionEditModal(true);
    }

    async function closeSectionEditModal() {
        if (typeof updateSpinner === "function") updateSpinner(false);
        const nextSelectedCustomId = editingCustomSectionId;
        setShowSectionEditModal(false);
        setEditingSectionId(null);
        setEditingCustomSectionId(null);
        setEditingLanguageAbbrev(null);
        await loadCustomSectionsAsync();
        if (nextSelectedCustomId) {
            setSelectedCustomSectionId(Number(nextSelectedCustomId));
        }
    }

    async function onDeleteSectionContent(e, section) {
        e.stopPropagation();

        const sectionContentId = Number(section?.sectionContentId || section?.id);
        if (!sectionContentId) {
            toast.error("Unable to delete: invalid section content id.");
            return;
        }

        setDeletingSectionContentId(sectionContentId);
        if (typeof updateSpinner === "function") updateSpinner(true);
        try {
            const resp = await RightTimeApi.deleteAsync(`/SectionContent/${sectionContentId}`);
            if (resp?.ok) {
                toast.success("Customized section deleted.");
                await loadCustomSectionsAsync();
            } else {
                toast.error("Failed to delete customized section.");
            }
        } catch {
            toast.error("Error deleting customized section.");
        } finally {
            setDeletingSectionContentId(null);
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    async function onDeleteCompanyContent(e, content) {
        e.stopPropagation();

        const contentId = getSelectableId(content);
        if (!contentId) {
            toast.error("Unable to delete: invalid content id.");
            return;
        }

        if (contentId === Number(selectedCustomSectionId || 0)) {
            toast.error("Deselect this content before deleting it.");
            return;
        }

        setPendingDeleteCompanyContent(content);
        setShowDeleteCompanyContentConfirm(true);
    }

    function handleDeleteCompanyContentCancel() {
        setShowDeleteCompanyContentConfirm(false);
        setPendingDeleteCompanyContent(null);
    }

    async function handleDeleteCompanyContentConfirm() {
        const contentId = getSelectableId(pendingDeleteCompanyContent);
        setShowDeleteCompanyContentConfirm(false);
        setPendingDeleteCompanyContent(null);

        if (!contentId) {
            toast.error("Unable to delete: invalid content id.");
            return;
        }

        setDeletingCompanyContentId(contentId);
        if (typeof updateSpinner === "function") updateSpinner(true);
        try {
            const resp = await RightTimeApi.deleteAsync(`/CompanyContent/${contentId}`);
            if (resp?.ok) {
                toast.success("Content deleted.");
                await loadContentOptionsAsync();
            } else {
                toast.error("Failed to delete content.");
            }
        } catch {
            toast.error("Error deleting content.");
        } finally {
            setDeletingCompanyContentId(null);
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    async function createSectionContentAsync(name) {
        const trimmedName = name?.trim() || "";
        if (!trimmedName) return;
        if (!sectionId) {
            toast.error("Unable to create section content: missing section id.");
            return;
        }

        if (typeof updateSpinner === "function") updateSpinner(true);
        try {
            const requestCompanyId = Number(companyId || 0);
            const resp = await RightTimeApi.postAsync("/SectionContent", {
                name: trimmedName,
                sectionId: Number(sectionId),
                companyId: requestCompanyId > 0 ? requestCompanyId : null,
            });

            if (resp?.ok) {
                const newId = Number(
                    resp?.jsonData?.id ||
                    resp?.jsonData?.sectionContentId ||
                    resp?.data?.id ||
                    resp?.data?.sectionContentId ||
                    0
                ) || null;

                toast.success("Section content created.");
                await loadCustomSectionsAsync();
                if (newId) {
                    setSelectedCustomSectionId(newId);
                }
            } else {
                toast.error("Failed to create section content.");
            }
        } catch {
            toast.error("Error creating section content.");
        } finally {
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    function startDuplicateSectionContent(e, section) {
        e.stopPropagation();
        const sectionContentId = Number(section?.sectionContentId || section?.id) || null;
        if (!sectionContentId) {
            toast.error("Unable to duplicate: invalid section content id.");
            return;
        }

        setDuplicateSourceSectionContentId(sectionContentId);
        setShowDuplicateModal(true);
    }

    async function duplicateSectionContentAsync(name) {
        const trimmedName = name?.trim() || "";
        if (!trimmedName) return;
        if (!duplicateSourceSectionContentId) {
            toast.error("Unable to duplicate: missing source section content id.");
            return;
        }

        if (typeof updateSpinner === "function") updateSpinner(true);
        try {
            const requestCompanyId = Number(companyId || 0);
            const resp = await RightTimeApi.postAsync(`/SectionContent/${duplicateSourceSectionContentId}/copy`, {
                name: trimmedName,
                companyId: requestCompanyId > 0 ? requestCompanyId : null,
            });

            if (resp?.ok) {
                const newId = Number(
                    resp?.jsonData?.id ||
                    resp?.jsonData?.sectionContentId ||
                    resp?.data?.id ||
                    resp?.data?.sectionContentId ||
                    0
                ) || null;

                toast.success("Section content duplicated.");
                setShowDuplicateModal(false);
                setDuplicateSourceSectionContentId(null);
                await loadCustomSectionsAsync();
                if (newId) {
                    setSelectedCustomSectionId(newId);
                }
            } else {
                toast.error("Failed to duplicate section content.");
            }
        } catch {
            toast.error("Error duplicating section content.");
        } finally {
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    async function onSectionCompanyChange(e, section) {
        e.stopPropagation();

        const sectionContentId = getSelectableId(section);
        if (!sectionContentId) {
            toast.error("Unable to update company: invalid section content id.");
            return;
        }

        const selectedValue = e.target.value;
        const nextCompanyId = selectedValue === "" ? null : Number(selectedValue);
        const requestCompanyId = Number.isFinite(nextCompanyId) && nextCompanyId > 0 ? nextCompanyId : null;

        setUpdatingCompanySectionContentId(sectionContentId);
        if (typeof updateSpinner === "function") updateSpinner(true);

        try {
            const resp = await RightTimeApi.putAsync(`/SectionContent/${sectionContentId}/Company`, {
                companyId: requestCompanyId,
            });

            if (!resp?.ok) {
                toast.error("Failed to update section content company.");
                return;
            }

            const mappedCompanyName = requestCompanyId
                ? (companies.find((company) => company.id === requestCompanyId)?.name || "")
                : "";

            setCustomSections((prev) => prev.map((item) => {
                if (getSelectableId(item) !== sectionContentId) return item;
                return {
                    ...item,
                    companyId: requestCompanyId,
                    CompanyId: requestCompanyId,
                    companyName: mappedCompanyName,
                    CompanyName: mappedCompanyName,
                };
            }));

            toast.success("Section content company updated.");
        } catch {
            toast.error("Error updating section content company.");
        } finally {
            setUpdatingCompanySectionContentId(null);
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    const footer = (
        <div className="footerRight">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
            </button>
            <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeselectClick}
                disabled={isSelecting}
            >
                Deselect
            </button>
            <button
                type="button"
                className="btn btn-primary"
                onClick={handleSelectClick}
                disabled={!selectedSection || isSelecting}
            >
                {isSelecting ? "Selecting..." : "Select"}
            </button>
        </div>
    );

    const filteredContentOptions = useMemo(() => {
        if (!isContentMode) return [];
        if (!hasValidContentTypeId) return [];

        return customSections.filter((content) => Number(content?.contentTypeId || content?.contentType || 0) === targetContentTypeId);
    }, [customSections, isContentMode, hasValidContentTypeId, targetContentTypeId]);

    useEffect(() => {
        if (!isOpen || !isContentMode) return;

        const preferredId = Number(selectedContentId || 0);
        if (preferredId > 0) {
            setSelectedCustomSectionId(preferredId);
        }
    }, [isOpen, isContentMode, selectedContentId, filteredContentOptions]);

    const currentCompanyId = Number(companyId || 0);
    const canImportSectionContent = !isContentMode && currentCompanyId > 0;
    const canImportCompanyContent = isContentMode && currentCompanyId > 0;

    const importableSections = useMemo(() => {
        if (!canImportSectionContent) return [];

        return customSections.filter((section) => {
            const sectionContentId = getSelectableId(section);
            const sourceCompanyId = getSectionCompanyId(section);
            return (
                Number(sectionContentId || 0) > 0 &&
                Number(sourceCompanyId || 0) > 0 &&
                Number(sourceCompanyId) !== currentCompanyId
            );
        });
    }, [customSections, canImportSectionContent, currentCompanyId]);

    function getCompanyNameForSection(section) {
        const sourceCompanyId = getSectionCompanyId(section);
        if (!sourceCompanyId) return "";

        return (
            section?.companyName ||
            section?.CompanyName ||
            companies.find((company) => Number(company?.id || 0) === Number(sourceCompanyId))?.name ||
            ""
        );
    }

    function onOpenImportModal() {
        if (!canImportSectionContent) {
            toast.error("Import is only available when a current company is selected.");
            return;
        }

        setShowImportModal(true);
    }

    function onOpenContentImportModal() {
        if (!canImportCompanyContent) {
            toast.error("Import is only available when a current company is selected.");
            return;
        }

        if (!hasValidContentTypeId) {
            toast.error("Import requires a content type.");
            return;
        }

        setImportContentTypeId(targetContentTypeId);
        setShowContentImportModal(true);
    }

    async function onContentImportedAsync() {
        await loadContentOptionsAsync();
    }

    async function onImportSectionContent(section) {
        const sourceSectionContentId = getSelectableId(section);
        if (!sourceSectionContentId || !canImportSectionContent) return;

        setImportingSectionContentId(sourceSectionContentId);
        if (typeof updateSpinner === "function") updateSpinner(true);

        try {
            const copyResp = await RightTimeApi.postAsync(`/SectionContent/${sourceSectionContentId}/copyToCompany`, {
                companyId: currentCompanyId,
            });

            if (!copyResp?.ok) {
                toast.error("Failed to import section content.");
                return;
            }

            const copiedContentId = Number(
                copyResp?.jsonData?.sectionContentId ||
                copyResp?.jsonData?.id ||
                copyResp?.data?.sectionContentId ||
                copyResp?.data?.id ||
                0
            ) || null;

            if (!copiedContentId) {
                toast.error("Import completed but no section content id was returned.");
                return;
            }

            setShowImportModal(false);
            await loadCustomSectionsAsync();
            setSelectedCustomSectionId(copiedContentId);
            toast.success("Section content imported. Review and click Select to apply it.");
        } catch {
            toast.error("Error importing section content.");
        } finally {
            setImportingSectionContentId(null);
            if (typeof updateSpinner === "function") updateSpinner(false);
        }
    }

    async function onOpenCreateContentModal() {
        setContentEditId(0);
        setIsContentEditModalOpen(true);
    }

    async function onOpenEditContentModal(e, content) {
        e.stopPropagation();
        const id = Number(content?.contentId || content?.id || 0);
        if (!id) return;

        setContentEditId(id);
        setIsContentEditModalOpen(true);
    }

    async function onCloseContentEditModal() {
        setIsContentEditModalOpen(false);
        setContentEditId(0);
        await loadContentOptionsAsync();
    }

    return (
        <>
            <Modal
                isOpen={!!isOpen}
                onClose={onClose}
                title={isContentMode ? "Select Content" : "Select Customized Section"}
                className="sectionSelectModal selectModal"
                footer={footer}
                zIndex={1100}
            >
                {isLoading ? (
                    <div>{isContentMode ? "Loading content options..." : "Loading customized sections..."}</div>
                ) : (
                    <div className="tableWrapper dimensionTable scrollable sectionSelectTable">
                        <table>
                            <caption>
                              <span className="title">{isContentMode ? "Content" : "Section Contents"}</span>
                                    <span className="captionRight">
                                        {canImportSectionContent && (
                                            <TbTableImport
                                                className="actionIcon"
                                                data-help-id="importSectionContent"
                                                onClick={onOpenImportModal}
                                                title="Import section content from another company"
                                            />
                                        )}
                                        {canImportCompanyContent && (
                                            <TbTableImport
                                                className="actionIcon"
                                                data-help-id="importCompanyContent"
                                                onClick={onOpenContentImportModal}
                                                title="Import content from another company"
                                            />
                                        )}
                                        <FaPlusSquare
                                            className="actionIcon plusIcon"
                                            data-help-id={isContentMode ? "createContent" : "createSectionContent"}
                                            onClick={isContentMode ? onOpenCreateContentModal : (() => setShowCreateModal(true))}
                                            title={isContentMode ? "Create new content" : "Create new section content"}
                                        />
                                    </span>
                            </caption>
                            <thead>
                                {isContentMode ? (
                                    <tr>
                                        <th>ID</th>
                                        <th>Type</th>
                                        <th>Name</th>
                                        <th>Languages</th>
                                        <th></th>
                                    </tr>
                                ) : (
                                    <tr className="sectionContentsHeaderRow">
                                        <th>ID</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Name</th>
                                        <th style={{ whiteSpace: "nowrap" }}>Company</th>
                                        {supportedLanguages.map((language) => (
                                            <th
                                                key={getLanguageAbbrev(language) || getLanguageDisplayName(language)}
                                                className="languageColumnHeader"
                                            >
                                                <span className="languageColumnHeaderText">
                                                    {getLanguageDisplayName(language)}
                                                </span>
                                            </th>
                                        ))}
                                        <th></th>
                                    </tr>
                                )}
                            </thead>
                            <tbody>
                                {isContentMode
                                    ? (filteredContentOptions.length > 0 ? (
                                        filteredContentOptions.map((content, index) => {
                                            const selectableId = getSelectableId(content);
                                            const isSelected = selectableId === Number(selectedCustomSectionId);
                                            const rowKey = selectableId || `${getSectionName(content)}-${index}`;
                                            const languageText = Array.isArray(content?.languages)
                                                ? content.languages
                                                    .map((item) => contentData.languages.find((lang) => Number(lang?.id) === Number(item?.languageId))?.abbrev || "")
                                                    .filter(Boolean)
                                                    .join(", ")
                                                : "";
                                            const isDeleting = deletingCompanyContentId !== null && deletingCompanyContentId === selectableId;
                                            const canDelete = selectableId > 0 && !isSelected;

                                            return (
                                                <tr
                                                    key={rowKey}
                                                    ref={isSelected ? scrollToSelected : null}
                                                    onClick={() => setSelectedCustomSectionId(selectableId)}
                                                    className={isSelected ? "active" : ""}
                                                >
                                                    <td>{Number(content?.contentId || content?.id || 0) || ""}</td>
                                                    <td>{content?.contentTypeName || ""}</td>
                                                    <td>{content?.name || ""}</td>
                                                    <td>{languageText}</td>
                                                    <td>
                                                        <MdEdit
                                                            className="actionIcon"
                                                            onClick={(e) => onOpenEditContentModal(e, content)}
                                                            title="Edit Content"
                                                        />
                                                        {canDelete ? (
                                                            <IoTrashOutline
                                                                className="actionIcon deleteIcon"
                                                                onClick={(e) => onDeleteCompanyContent(e, content)}
                                                                title={isDeleting ? "Deleting..." : "Delete Content"}
                                                            />
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={3 + supportedLanguages.length + 1} style={{ textAlign: "center" }}>
                                                No content found.
                                            </td>
                                        </tr>
                                    ))
                                    : (filteredCustomSections.length > 0 ? (
                                        filteredCustomSections.map((section, index) => {
                                            const selectableId = getSelectableId(section);
                                            const id = Number(section?.id || section?.Id || selectableId) || 0;
                                            const isSelected = selectableId === Number(selectedCustomSectionId);
                                            const rowKey = selectableId || `${getSectionName(section)}-${index}`;
                                            const isUsed = section?.usageCount > 0;
                                            const sectionContentId = Number(section?.sectionContentId || section?.SectionContentId || section?.id || section?.Id) || null;
                                            const isDeleting = deletingSectionContentId !== null && deletingSectionContentId === sectionContentId;
                                            const sectionCompanyId = getSectionCompanyId(section);
                                            const isUpdatingCompany = updatingCompanySectionContentId !== null && updatingCompanySectionContentId === sectionContentId;

                                            return (
                                                <tr
                                                    key={rowKey}
                                                    ref={isSelected ? scrollToSelected : null}
                                                    onClick={() => setSelectedCustomSectionId(selectableId)}
                                                    className={isSelected ? "sectionContentsRow active" : "sectionContentsRow"}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <td>{id || ""}</td>
                                                    <td style={{ whiteSpace: "nowrap" }}>
                                                        {getSectionName(section)}
                                                    </td>
                                                    <td style={{ whiteSpace: "nowrap" }}>
                                                        <select
                                                            value={sectionCompanyId ?? ""}
                                                            onChange={(e) => onSectionCompanyChange(e, section)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            disabled={isUpdatingCompany}
                                                            title="Set company"
                                                        >
                                                            <option value=""></option>
                                                            {companies.map((company) => (
                                                                <option key={company.id} value={company.id}>
                                                                    {company.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    {supportedLanguages.map((language) => {
                                                        const abbrev = getLanguageAbbrev(language);
                                                        const content = getSectionLanguageContent(section, abbrev, supportedLanguages);
                                                        const hasContent = hasSectionContent(content);
                                                        return (
                                                            <td
                                                                key={`${rowKey}-${abbrev}`}
                                                                className="languageColumnCell"
                                                            >
                                                                {hasContent ? (
                                                                    <FaCheck
                                                                        className="actionIcon"
                                                                        onClick={(e) => openSectionEditModal(e, section, abbrev)}
                                                                        title={`Edit ${getLanguageDisplayName(language)} section`}
                                                                    />
                                                                ) : null}
                                                            </td>
                                                        );
                                                    })}
                                                    <td style={{ textAlign: "left" }}>
                                                        {sectionContentId ? (
                                                            <MdEdit
                                                                className="actionIcon"
                                                                onClick={(e) => openSectionEditModal(e, section)}
                                                                title="Edit Section"
                                                            />
                                                        ) : ""}
                                                        {sectionContentId ? (
                                                            <LuCopyPlus
                                                                className="actionIcon"
                                                                onClick={(e) => startDuplicateSectionContent(e, section)}
                                                                title="Duplicate Section Content"
                                                            />
                                                        ) : ""}
                                                        {!isUsed && sectionContentId ? (
                                                            <IoTrashOutline
                                                                className="actionIcon"
                                                                onClick={(e) => onDeleteSectionContent(e, section)}
                                                                title={isDeleting ? "Deleting..." : "Delete Section Content"}
                                                            />
                                                        ) : ""}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={4 + supportedLanguages.length} style={{ textAlign: "center" }}>
                                                No customized sections found.
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Modal>

            <ContentViewModal
                isOpen={showContentViewModal}
                onClose={onCloseContentViewModal}
                title={contentViewTitle}
                content={contentViewHtml}
            />

            <Modal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                title="Import Section Content"
                className="selectModal"
                zIndex={5000}
            >
                <div className="tableWrapper dimensionTable scrollable sectionSelectTable">
                    <table>
                        <caption>
                            <span className="title">Choose Source Content</span>
                        </caption>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Company</th>
                            </tr>
                        </thead>
                        <tbody>
                            {importableSections.length > 0 ? importableSections.map((section, index) => {
                                const sectionContentId = getSelectableId(section);
                                const companyName = getCompanyNameForSection(section);
                                const sectionName = getSectionName(section);
                                const rowKey = sectionContentId || `${sectionName}-${index}`;
                                const isImporting = importingSectionContentId !== null && importingSectionContentId === sectionContentId;

                                return (
                                    <tr
                                        key={rowKey}
                                        onClick={() => onImportSectionContent(section)}
                                        className={isImporting ? "active" : ""}
                                        title={isImporting ? "Importing..." : "Import this section content"}
                                    >
                                        <td>{sectionContentId || ""}</td>
                                        <td>{sectionName}</td>
                                        <td>{companyName}</td>
                                    </tr>
                                );
                            }) : (
                                <tr>
                                    <td colSpan={3} style={{ textAlign: "center" }}>
                                        No section content found to import from other companies.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Modal>

            <TextInputModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSubmit={(value) => {
                    createSectionContentAsync(value);
                }}
                headerText="Create Section Content"
                labelText="Name"
                placeholderText="Enter section content name..."
                buttonText="Create"
                currentValue={
                    typeof sectionName === "string" && sectionName.trim()
                        ? `${sectionName.trim()} Content`
                        : ""
                }
                zIndex={5000}
            />

            <TextInputModal
                isOpen={showDuplicateModal}
                onClose={() => {
                    setShowDuplicateModal(false);
                    setDuplicateSourceSectionContentId(null);
                }}
                onSubmit={(value) => {
                    duplicateSectionContentAsync(value);
                }}
                headerText="Duplicate Section Content"
                labelText="Copy Name"
                placeholderText="Enter name for copied section content..."
                buttonText="Create"
                zIndex={5000}
                closeOnSubmit={false}
            />

            <ContentEditModal
                isOpen={isContentEditModalOpen}
                onClose={onCloseContentEditModal}
                companyId={Number(companyId) || 0}
                companyContentId={contentEditId}
                contentTypeId={Number(contentTypeId || 0) || undefined}
                zIndex={3000}
            />

            <SectionEditModal
                isOpen={showSectionEditModal}
                onClose={closeSectionEditModal}
                sectionContentId={editingSectionId}
                languageAbbrev={editingLanguageAbbrev || undefined}
                updateSpinner={updateSpinner}
            />

            <ContentImportModal
                isOpen={showContentImportModal}
                onClose={() => {
                    setShowContentImportModal(false);
                    setImportContentTypeId(null);
                }}
                companyId={currentCompanyId}
                contentTypeId={importContentTypeId ?? targetContentTypeId}
                updateSpinner={updateSpinner}
                onImported={onContentImportedAsync}
                zIndex={5000}
            />

            <ConfirmModal
                isOpen={showDeleteCompanyContentConfirm}
                onConfirm={handleDeleteCompanyContentConfirm}
                onCancel={handleDeleteCompanyContentCancel}
                title="Delete Content"
                message={`Are you sure you want to delete${pendingDeleteCompanyContent?.name ? ` "${pendingDeleteCompanyContent.name}"` : " this content"}?`}
                confirmText="Yes, Delete Content"
                dangerText="This will permanently remove this content."
                showSecondStep={false}
                zIndex={6000}
            />
        </>
    );
}


